/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useContext } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';

import { token } from '@atlaskit/tokens';

import { getBorderForValue } from '../../../color-palette-ramps/get-border-for-value';
import { getTextColorForBackground } from '../../../color-palette-ramps/get-text-color-for-background';
import { normaliseThemeColorMode } from '../normalise-theme-color-mode';
import { tokenColorModeValues } from '../token-color-mode-values';
import type { TransformedTokenMerged } from '../types';
import { BaseTokenButtonStyle } from './base-token-button';
import { ThemeBoundaryContext } from './token-button-value';

export type ColorTokenCategoryProps = Pick<TransformedTokenMerged, 'value'> & {
	children?: ReactNode;
};

export const Color = (props: ColorTokenCategoryProps): React.JSX.Element => {
	const { children, value } = props;
	const applyTheme = useContext(ThemeBoundaryContext);

	const backgroundCss = {
		backgroundColor: token('elevation.surface'),
		backgroundImage: `linear-gradient(
          0deg,
          ${value} 0%,
          ${value} 100%
        ),
        linear-gradient(
          45deg,
          ${token('elevation.surface.sunken')} 25%,
          transparent 25%
        ),
        linear-gradient(
          135deg,
          ${token('elevation.surface.sunken')} 25%,
          transparent 25%
        ),
        linear-gradient(
          45deg,
          transparent 75%,
          ${token('elevation.surface.sunken')} 75%
        ),
        linear-gradient(
          135deg,
          transparent 75%,
          ${token('elevation.surface.sunken')} 75%
        )`,
		backgroundPosition: '0px 0px, 0px 0px, 0.5rem 0px, 0.5rem -0.5rem, 0px 0.5rem',
		backgroundSize: '100% 100%, 1rem 1rem, 1rem 1rem, 1rem 1rem, 1rem 1rem',
		'&:hover, &:focus': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.pressed'),
		},
	};

	const normalisedColorModel = normaliseThemeColorMode(applyTheme);
	const background = tokenColorModeValues['elevation.surface'][normalisedColorModel];

	return (
		<BaseTokenButtonStyle
			css={[
				{
					position: 'relative',
					zIndex: 0,
					...backgroundCss,
					overflow: 'hidden',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					color: getTextColorForBackground(value),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					outline: getBorderForValue(value, background),
					'&:hover, &:focus': {
						...backgroundCss,
					},
					'&:active': {
						...backgroundCss,
					},
				},
			]}
		>
			{children}
		</BaseTokenButtonStyle>
	);
};
